import React, { useEffect, useRef } from 'react';
import * as Tabs from '@radix-ui/react-tabs';

import { createStyles } from '../../../utils/createStyle';

import { ShopNavOption } from './ShopNavOption';

import { Routes } from 'routes';
import { useHistory, useLocation } from 'react-router-dom';
import { logMainShopNavClick } from 'config/analytics';
import { IS_FLIPGIVE } from 'common/env';

const style = createStyles({
    listContainer: [
        'flex flex-nowrap items-center justify-start overflow-x-auto min-w-0',
        'sm:pl-0 sm:pr-0',
        'justify-center md:overflow-x-visible',
        'text-center text-brand',
        'no-scrollbar',
    ],
    container: [
        'flex-grow lg:flex-none lg:w-auto min-w-0',
        'lg:order-2 order-3 w-full text-center mt-4 md:mt-0',
        'border-t-2 border-solid  border-grey-light lg:border-none',
        'pt-2 mt-2 md:-mx-8 lg:mx-0 lg:pt-0 lg:mt-0 ',
    ],
});

export enum NavOptions {
    GIFT_CARDS = 'Gift Cards',
    SHOP_ONLINE = 'Shop Online',
    BOOK_TRAVEL = 'Book Travel',
    LOCAL_DINING = 'Local Dining',
}

const LOCAL_DINING_URL =
    'https://www.raiseright.com/how-it-works/local-dining?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=local_dining';

export interface ShopNavOptionsProps {
    selectedTab?:
        | Routes.Shop
        | Routes.ShopOnline
        | Routes.ShopGiftCards
        | Routes.ShopDiscover
        | Routes.ShopDiscoverTravel
        | undefined;
}

export const ShopNavOptions = ({ selectedTab }: ShopNavOptionsProps) => {
    const history = useHistory();
    const listRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const inTravelPath = location.pathname === Routes.ShopDiscoverTravel;
    const effectiveSelectedTab = inTravelPath ? Routes.ShopDiscoverTravel : selectedTab;

    const onBuyGiftCardsClick = () => {
        logMainShopNavClick({ tab: NavOptions.GIFT_CARDS });
        history.push(Routes.ShopGiftCards);
    };
    const onShopOnlineClick = () => {
        logMainShopNavClick({ tab: NavOptions.SHOP_ONLINE });
        history.push(Routes.ShopOnline);
    };

    const onBookTravelClick = () => {
        logMainShopNavClick({ tab: NavOptions.BOOK_TRAVEL });
        history.push(Routes.ShopDiscoverTravel);
    };

    const onLocalDiningClick = () => {
        logMainShopNavClick({ tab: NavOptions.LOCAL_DINING });
        window.location.assign(LOCAL_DINING_URL);
    };

    const NAV_ITEMS = [
        {
            tabId: NavOptions.GIFT_CARDS,
            title: NavOptions.GIFT_CARDS,
            icon: null,
            onClick: onBuyGiftCardsClick,
            isSelected: () => Routes.ShopGiftCards === selectedTab,
        },
        {
            tabId: NavOptions.SHOP_ONLINE,
            title: NavOptions.SHOP_ONLINE,
            icon: null,
            onClick: onShopOnlineClick,
            isSelected: () => Routes.ShopOnline === selectedTab,
        },
        {
            tabId: NavOptions.BOOK_TRAVEL,
            title: NavOptions.BOOK_TRAVEL,
            icon: null,
            onClick: onBookTravelClick,
            isSelected: () => inTravelPath,
        },
        // Local Dining is RaiseRight-only. Book Travel is intentionally available on both brands.
        ...(IS_FLIPGIVE
            ? []
            : [
                  {
                      tabId: NavOptions.LOCAL_DINING,
                      title: NavOptions.LOCAL_DINING,
                      icon: null,
                      onClick: onLocalDiningClick,
                      isSelected: () => false,
                  },
              ]),
    ];

    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl) {
            return;
        }

        const activeTab = listEl.querySelector<HTMLElement>('[data-state="active"]');
        if (activeTab) {
            activeTab.scrollIntoView({ inline: 'end', block: 'nearest', behavior: 'smooth' });
        }
    }, [effectiveSelectedTab]);

    return (
        <Tabs.Root className={style.container} value={effectiveSelectedTab}>
            <Tabs.List ref={listRef} className={style.listContainer}>
                {NAV_ITEMS.map((item) => (
                    <ShopNavOption
                        key={item.tabId}
                        icon={item.icon}
                        title={item.title}
                        onClick={item.onClick}
                        selected={item.isSelected()}
                        tabId={item.tabId}
                    />
                ))}
            </Tabs.List>
        </Tabs.Root>
    );
};
